import React, { useEffect, useState } from "react";
import { Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstTabs from "../../components/DstTabs/DstTabs";
import MultiSelectDropdown from "../../components/MultiSelectDropdown/MultiSelectDropdown";
import { useSession } from "../../contexts/SessionContext";
import { PermissionComponent } from "../../services/PermissionGuard";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { CursusStage, Flag } from "./Course.d";
// import Meetings from "./components/Meetings/Meetings";
// import PedagogicalPath from "./components/PedagogicalPath/PedagogicalPath";
import { fetchCursusStages } from "./Course.function";
import AboutCourse from "./components/AboutCourse/AboutCourse";
import { CourseStageFlagsPopup } from "./components/CourseStageFlagsPopup/CourseStageFlagsPopup";

const Course = () => {
  const { language } = useSession();
  const navigate = useNavigate();
  const [activeKey, setActiveKey] = useState<string>("about_course");
  const { course_id } = useParams();
  const [cursusStages, setCursusStages] = useState<CursusStage[]>([]);
  const [selectedStageId, setSelectedStageId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [cursusStagesFetchFailed, setCursusStagesFetchFailed] = useState(false);
  const [openFlagsPopup, setOpenFlagsPopup] = useState(false);

  const selectedCursusStage = cursusStages.find((stage) => stage.id === selectedStageId);

  useEffect(() => {
    if (cursusStages.length > 0 && !cursusStages.find((stage) => stage.id === selectedStageId)) {
      setSelectedStageId(cursusStages[0].id);
    }
  }, [cursusStages, selectedStageId]);

  useEffect(() => {
    fetchCursusStages(course_id!, setCursusStages, setLoading, language, setCursusStagesFetchFailed);
  }, [course_id, language]);

  if (loading) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-primary mb-3" role="status" />
        <div>{translate(language, "PAGES.COURSE.LOADING")}</div>
      </div>
    );
  }

  if (cursusStagesFetchFailed) {
    return (
      <div className="text-center mt-3">
        <h2>{translate(language, "PAGES.COURSE.ERRORS.NOT_FOUND")}</h2>
        <DstButton
          value={translate(language, "PAGES.COURSE.BUTTONS.ALL_COURSES")}
          clickFunction={(event: any) => completeNavigate(event, navigate, `/courses`)}
          btnClass="mt-3"
        />
      </div>
    );
  }

  if (cursusStages.length === 0 || !selectedCursusStage) {
    return (
      <div className="text-center mt-3">
        <h2>{translate(language, "PAGES.COURSE.ERRORS.NO_STAGE")}</h2>

        <DstButton
          value={translate(language, "PAGES.COURSE.BUTTONS.CREATE_STAGE")}
          btnClass="ms-2 mt-3"
          btnImageBefore={<span className="me-2 dst-icon-plus" />}
          variant="primary"
          clickFunction={() => alert("WIP")}
        />
      </div>
    );
  }

  const stageOptions = cursusStages.map((stage) => ({
    id: stage.id,
    name: `${stage.deployment_name || ""} - ${stage.cursus.name || ""} (#${stage.id})`,
  }));

  const tabsData = [
    {
      id: "about_course",
      title: (
        <React.Fragment>
          <span className="dst-icon-info-circle me-2" />
          {translate(language, "PAGES.COURSE.TABS.ABOUT_COURSE")}
        </React.Fragment>
      ),
      content: <AboutCourse course={selectedCursusStage} language={language} />,
    },
    // {
    //   id: "pedagogical_path",
    //   title: (
    //     <React.Fragment>
    //       <span className="dst-icon-book-open-01 me-2" />
    //       {translate(language, "PAGES.COURSE.TABS.PEDAGOGICAL_PATH")}
    //     </React.Fragment>
    //   ),
    //   content: <PedagogicalPath course={selectedCursusStage} language={language} />,
    // },
    // {
    //   id: "meetings",
    //   title: (
    //     <React.Fragment>
    //       <span className="dst-icon-video-recorder me-2" />
    //       {translate(language, "PAGES.COURSE.TABS.MEETINGS")}
    //     </React.Fragment>
    //   ),
    //   content: <Meetings />,
    // },
  ];

  return (
    <div className="m-3">
      <div className="d-flex align-items-lg-center justify-content-between mb-3">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <h1 className="me-2">{selectedCursusStage.cursus.name}</h1>
          <Badge bg={selectedCursusStage.is_disabled ? "secondary" : "success"} className="me-2 text-white">
            {selectedCursusStage.is_disabled ? (
              <div>
                <span className="dst-icon-alert-octagon me-1" />
                {translate(language, "PAGES.COURSE.STATUS.INACTIVE")}
              </div>
            ) : (
              <div>
                <span className="dst-icon-check me-1" />
                {translate(language, "PAGES.COURSE.STATUS.ACTIVE")}
              </div>
            )}
          </Badge>
          <MultiSelectDropdown
            items={stageOptions}
            language={language}
            label={
              stageOptions.find((option) => option.id === selectedStageId)
                ? `${translate(language, "PAGES.COURSE.SELECT_STAGE")}: ${
                    stageOptions.find((option) => option.id === selectedStageId)?.name
                  }`
                : translate(language, "PAGES.COURSE.SELECT_STAGE")
            }
            isSingleSelect
            badgeItemProperty="name"
            displayItem={(item) => item.name}
            defaultSelectedItems={[stageOptions.find((option) => option.id === selectedStageId)]}
            onConfirm={(selected) => setSelectedStageId(selected[0]?.id ?? null)}
            disabled={cursusStages.length < 2}
            borderToggle
          />
        </div>
        <div>
          <DstButton
            value={translate(language, "PAGES.COURSE.BUTTONS.DEPRECATE")}
            btnClass="ms-2"
            btnImageBefore={<span className="me-2 dst-icon-trash-01" />}
            variant="light"
            clickFunction={() => alert("WIP")}
          />
          <DstButton
            value={translate(language, "PAGES.COURSE.BUTTONS.DUPLICATE")}
            btnClass="ms-2"
            btnImageBefore={<span className="me-2 dst-icon-copy-03" />}
            variant="light"
            clickFunction={() => alert("WIP")}
          />
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <span className="s-bold">{translate(language, "PAGES.COURSE.FLAGS.TITLE")}</span>
        {selectedCursusStage.flags && selectedCursusStage.flags.length > 0 ? (
          selectedCursusStage.flags.map((flag: Flag) => (
            <OverlayTrigger
              key={flag.id}
              trigger={["hover", "focus"]}
              placement="top"
              overlay={<Tooltip id={`tooltip-flag-${flag.id}`}>{flag.name}</Tooltip>}
            >
              <span className={`dst-icon-${flag.icon_name} me-2 blue-font`} />
            </OverlayTrigger>
          ))
        ) : (
          <span className="text-muted">-</span>
        )}
        <PermissionComponent neededPermission={[{ methods: ["PUT"], route: "/cursus/stages/*/flags" }]}>
          <DstButton
            value={translate(language, "PAGES.COURSE.FLAGS.EDIT")}
            btnImageBefore={<span className="dst-icon-edit-02 me-2" />}
            variant="light"
            clickFunction={() => setOpenFlagsPopup(true)}
          />
        </PermissionComponent>
      </div>

      <DstTabs
        activeKey={activeKey}
        variant="primary"
        onSelect={(key) => {
          if (key) {
            setActiveKey(key);
          }
        }}
        tabsData={tabsData}
      />

      <CourseStageFlagsPopup
        language={language}
        openPopup={openFlagsPopup}
        setOpenPopup={setOpenFlagsPopup}
        cursusStage={selectedCursusStage}
        onFlagsUpdated={(flags) => {
          setCursusStages((previousStages) =>
            previousStages.map((stage) => (stage.id === selectedCursusStage.id ? { ...stage, flags } : stage))
          );
        }}
      />
    </div>
  );
};

export default Course;
